import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Car, LogOut, Users, Package, BarChart3, Settings, Menu, Search, Shield, 
  Bell, Activity, Eye, UserX, UserCheck, Trash2, ChevronDown, ChevronUp,
  Flag, AlertTriangle, Send, Megaphone, Server, Database, X, Edit, DollarSign,
  TrendingUp, ArrowUpRight, Download, Zap, RefreshCw, ToggleLeft, ToggleRight,
  PieChart, UserCog, Mail, Moon, Sun, FileCheck, CheckCircle, XCircle, Clock, ExternalLink,
  Key, Unlock, History
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import LanguagePicker from '../components/LanguagePicker';
import NotificationBell from '../components/NotificationBell';
import NotificationSettings from '../components/NotificationSettings';
import { toast } from 'sonner';
import CommandPalette from '../components/CommandPalette';
import '../styles/admin-dark.css';
import api from '../utils/api';

const ADMIN_NAV = [
  { id: 'overview', icon: BarChart3, tKey: 'admin.overview' },
  { id: 'users', icon: Users, tKey: 'admin.users' },
  { id: 'id-verification', icon: FileCheck, tKey: 'admin.idVerification' },
  { id: 'vehicles', icon: Car, tKey: 'admin.vehicles' },
  { id: 'revenue', icon: DollarSign, tKey: 'admin.revenue' },
  { id: 'moderation', icon: Shield, tKey: 'admin.moderation' },
  { id: 'announcements', icon: Megaphone, tKey: 'admin.announcements' },
  { id: 'digest', icon: Mail, tKey: 'admin.emailDigest' },
  { id: 'activity', icon: Activity, tKey: 'admin.activityLog' },
  { id: 'health', icon: Server, tKey: 'admin.systemHealth' },
  { id: 'platform', icon: Settings, tKey: 'admin.platformSettings' },
  { id: 'settings', icon: Bell, tKey: 'admin.notifications' },
];

const ROLE_COLORS = {
  admin: 'bg-purple-100 text-purple-700',
  dealer: 'bg-blue-100 text-blue-700',
  buyer: 'bg-emerald-100 text-emerald-700',
  seller: 'bg-amber-100 text-amber-700',
};

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [growth, setGrowth] = useState(null);
  const [users, setUsers] = useState([]);
  const [usersTotal, setUsersTotal] = useState(0);
  const [vehicles, setVehicles] = useState([]);
  const [vehiclesTotal, setVehiclesTotal] = useState(0);
  const [listings, setListings] = useState([]);
  const [leads, setLeads] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [activityLog, setActivityLog] = useState([]);
  const [health, setHealth] = useState(null);
  const [revenue, setRevenue] = useState(null);
  const [platformSettings, setPlatformSettings] = useState(null);
  const [digestPrefs, setDigestPrefs] = useState(null);
  const [digestPreview, setDigestPreview] = useState(null);
  const [sendingDigest, setSendingDigest] = useState(false);
  const [loading, setLoading] = useState(true);
  const [userSearch, setUserSearch] = useState('');
  const [userRoleFilter, setUserRoleFilter] = useState('');
  const [showAnnDialog, setShowAnnDialog] = useState(false);
  const [annForm, setAnnForm] = useState({ title: '', message: '', target_role: 'all' });
  const [selectedUser, setSelectedUser] = useState(null);
  const [cmdOpen, setCmdOpen] = useState(false);
  const [dark, setDark] = useState(() => localStorage.getItem('admin-dark') === 'true');
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [idVerifications, setIdVerifications] = useState([]);
  const [idStatusFilter, setIdStatusFilter] = useState('pending');
  const [idPreviewUser, setIdPreviewUser] = useState(null);
  const [rejectNote, setRejectNote] = useState('');
  const [resetPasswordUser, setResetPasswordUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [loginHistoryUser, setLoginHistoryUser] = useState(null);
  const [loginHistory, setLoginHistory] = useState(null);

  // Dark mode effect
  useEffect(() => {
    localStorage.setItem('admin-dark', dark);
    const root = document.documentElement;
    if (dark) {
      root.style.setProperty('--admin-card', '#1a1a1a');
      root.style.setProperty('--admin-card-border', 'rgba(255,255,255,0.08)');
      root.style.setProperty('--admin-text', '#f0f0f0');
      root.style.setProperty('--admin-text-muted', '#888');
      root.style.setProperty('--admin-bg-hover', 'rgba(255,255,255,0.05)');
    } else {
      root.style.setProperty('--admin-card', '#ffffff');
      root.style.setProperty('--admin-card-border', 'rgb(243,244,246)');
      root.style.setProperty('--admin-text', '#111827');
      root.style.setProperty('--admin-text-muted', '#6b7280');
      root.style.setProperty('--admin-bg-hover', 'rgb(249,250,251)');
    }
  }, [dark]);

  // Ctrl+K / Cmd+K to open command palette
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') { e.preventDefault(); setCmdOpen(true); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const [statsRes, growthRes] = await Promise.all([
        api.get('/api/admin/stats'),
        api.get('/api/admin/growth')
      ]);
      setStats(statsRes.data);
      setGrowth(growthRes.data);
    } catch {} finally { setLoading(false); }
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      const params = { limit: 50 };
      if (userSearch) params.search = userSearch;
      if (userRoleFilter) params.role = userRoleFilter;
      const { data } = await api.get('/api/admin/users', { params });
      setUsers(data.users);
      setUsersTotal(data.total);
    } catch {}
  }, [userSearch, userRoleFilter]);

  const fetchVehicles = useCallback(async () => {
    try { const { data } = await api.get('/api/admin/vehicles', { params: { limit: 50 } }); setVehicles(data.vehicles); setVehiclesTotal(data.total); } catch {}
  }, []);

  const fetchModeration = useCallback(async () => {
    try {
      const [listRes, leadRes] = await Promise.all([
        api.get('/api/admin/moderation/listings'),
        api.get('/api/admin/moderation/leads')
      ]);
      setListings(listRes.data);
      setLeads(leadRes.data);
    } catch {}
  }, []);

  const fetchAnnouncements = useCallback(async () => {
    try { const { data } = await api.get('/api/admin/announcements'); setAnnouncements(data); } catch {}
  }, []);

  const fetchActivity = useCallback(async () => {
    try { const { data } = await api.get('/api/admin/activity-log'); setActivityLog(data); } catch {}
  }, []);

  const fetchHealth = useCallback(async () => {
    try { const { data } = await api.get('/api/admin/system-health'); setHealth(data); } catch {}
  }, []);

  const fetchRevenue = useCallback(async () => {
    try { const { data } = await api.get('/api/admin/revenue'); setRevenue(data); } catch {}
  }, []);

  const fetchPlatformSettings = useCallback(async () => {
    try { const { data } = await api.get('/api/admin/platform-settings'); setPlatformSettings(data); } catch {}
  }, []);

  const fetchDigest = useCallback(async () => {
    try {
      const [prefsRes, previewRes, schedRes] = await Promise.all([
        api.get('/api/admin/digest/preferences'),
        api.get('/api/admin/digest/preview'),
        api.get('/api/admin/digest/scheduler-status')
      ]);
      setDigestPrefs(prefsRes.data);
      setDigestPreview(previewRes.data);
      setSchedulerStatus(schedRes.data);
    } catch {}
  }, []);

  const fetchIdVerifications = useCallback(async () => {
    try {
      const params = {};
      if (idStatusFilter && idStatusFilter !== 'all') params.status = idStatusFilter;
      const { data } = await api.get('/api/admin/id-verifications', { params });
      setIdVerifications(data);
    } catch {}
  }, [idStatusFilter]);

  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => { if (activeTab === 'users') fetchUsers(); }, [activeTab, fetchUsers]);
  useEffect(() => { if (activeTab === 'vehicles') fetchVehicles(); }, [activeTab, fetchVehicles]);
  useEffect(() => { if (activeTab === 'moderation') fetchModeration(); }, [activeTab, fetchModeration]);
  useEffect(() => { if (activeTab === 'announcements') fetchAnnouncements(); }, [activeTab, fetchAnnouncements]);
  useEffect(() => { if (activeTab === 'activity') fetchActivity(); }, [activeTab, fetchActivity]);
  useEffect(() => { if (activeTab === 'health') fetchHealth(); }, [activeTab, fetchHealth]);
  useEffect(() => { if (activeTab === 'revenue') fetchRevenue(); }, [activeTab, fetchRevenue]);
  useEffect(() => { if (activeTab === 'platform') fetchPlatformSettings(); }, [activeTab, fetchPlatformSettings]);
  useEffect(() => { if (activeTab === 'digest') fetchDigest(); }, [activeTab, fetchDigest]);
  useEffect(() => { if (activeTab === 'id-verification') fetchIdVerifications(); }, [activeTab, fetchIdVerifications]);

  const handleRoleChange = async (userId, newRole) => {
    try { await api.put(`/api/admin/users/${userId}/role`, { role: newRole }); toast.success('Role updated'); fetchUsers(); } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const handleSuspend = async (userId, suspended) => {
    try { await api.put(`/api/admin/users/${userId}/suspend`, { suspended }); toast.success(suspended ? 'User suspended' : 'User reactivated'); fetchUsers(); } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Delete this user permanently?')) return;
    try { await api.delete(`/api/admin/users/${userId}`); toast.success('User deleted'); fetchUsers(); } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const handlePlanChange = async (userId, plan) => {
    try { await api.put(`/api/admin/users/${userId}/plan`, { plan }); toast.success('Plan updated'); fetchUsers(); setSelectedUser(null); } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const handleFlagListing = async (listingId, flagged, reason = '') => {
    try { await api.put(`/api/admin/moderation/listings/${listingId}/flag`, { flagged, reason }); toast.success(flagged ? 'Listing flagged' : 'Flag removed'); fetchModeration(); } catch (e) { toast.error('Failed'); }
  };
  const handleLeadStatus = async (leadId, status) => {
    try { await api.put(`/api/admin/moderation/leads/${leadId}/status`, { status }); toast.success('Status updated'); fetchModeration(); } catch (e) { toast.error('Failed'); }
  };
  const handleCreateAnn = async () => {
    if (!annForm.title || !annForm.message) { toast.error('Title and message required'); return; }
    try { await api.post('/api/admin/announcements', annForm); toast.success('Announcement created'); setShowAnnDialog(false); setAnnForm({ title: '', message: '', target_role: 'all' }); fetchAnnouncements(); } catch (e) { toast.error('Failed'); }
  };
  const handleDeleteAnn = async (id) => {
    try { await api.delete(`/api/admin/announcements/${id}`); toast.success('Deleted'); fetchAnnouncements(); } catch {}
  };

  const handleExportUsers = async () => {
    try {
      const { data } = await api.get('/api/admin/users', { params: { limit: 9999 } });
      const rows = [['Name','Email','Role','Plan','Status','Vehicles','Joined']];
      data.users.forEach(u => rows.push([u.name, u.email, u.role, u.subscription_plan || 'Free', u.suspended ? 'Suspended' : 'Active', u.vehicle_count || 0, u.created_at ? new Date(u.created_at).toLocaleDateString() : '']));
      const csv = rows.map(r => r.map(c => `"${c}"`).join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `users_export_${new Date().toISOString().split('T')[0]}.csv`; a.click();
      URL.revokeObjectURL(url);
      toast.success('Users exported');
    } catch { toast.error('Export failed'); }
  };

  const handleCmdNavigate = (tab, opts) => {
    setActiveTab(tab);
    if (opts?.search) setUserSearch(opts.search);
  };

  const handleImpersonate = async (userId, email) => {
    if (!window.confirm(`Impersonate ${email}? You'll be logged in as this user.`)) return;
    try {
      await api.post(`/api/admin/impersonate/${userId}`);
      toast.success(`Now viewing as ${email}`);
      window.location.href = '/dashboard';
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to impersonate'); }
  };

  const handleToggleSetting = async (key, currentVal) => {
    try {
      await api.put('/api/admin/platform-settings', { [key]: !currentVal });
      setPlatformSettings(prev => ({ ...prev, [key]: !currentVal }));
      toast.success(`${key.replace(/_/g, ' ')} ${!currentVal ? 'enabled' : 'disabled'}`);
    } catch { toast.error('Failed to update setting'); }
  };

  const handleUpdateSetting = async (key, value) => {
    try {
      await api.put('/api/admin/platform-settings', { [key]: value });
      setPlatformSettings(prev => ({ ...prev, [key]: value }));
      toast.success('Setting updated');
    } catch { toast.error('Failed to update setting'); }
  };

  const handleSendDigest = async () => {
    setSendingDigest(true);
    try {
      const { data } = await api.post('/api/admin/digest/send', {
        period: digestPrefs?.frequency || 'daily',
        email: digestPrefs?.email || user?.email
      });
      toast.success(data.message);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to send digest'); }
    finally { setSendingDigest(false); }
  };

  const handleUpdateDigestPref = async (key, value) => {
    try {
      await api.put('/api/admin/digest/preferences', { [key]: value });
      setDigestPrefs(prev => ({ ...prev, [key]: value }));
      toast.success('Preference updated');
    } catch { toast.error('Failed to update preference'); }
  };

  const handleApproveId = async (userId, email) => {
    try {
      await api.put(`/api/admin/id-verifications/${userId}/approve`);
      toast.success(`ID approved for ${email}`);
      setIdPreviewUser(null);
      fetchIdVerifications();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to approve'); }
  };

  const handleRejectId = async (userId, email) => {
    try {
      await api.put(`/api/admin/id-verifications/${userId}/reject`, { note: rejectNote });
      toast.success(`ID rejected for ${email}`);
      setIdPreviewUser(null);
      setRejectNote('');
      fetchIdVerifications();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to reject'); }
  };

  const handleResetPassword = async () => {
    if (!resetPasswordUser || newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    try {
      await api.put(`/api/admin/users/${resetPasswordUser.id}/reset-password`, { new_password: newPassword });
      toast.success(`Password reset for ${resetPasswordUser.email}`);
      setResetPasswordUser(null);
      setNewPassword('');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to reset password'); }
  };

  const handleViewLoginHistory = async (u) => {
    setLoginHistoryUser(u);
    setLoginHistory(null);
    try {
      const { data } = await api.get(`/api/admin/users/${u.id}/login-history`);
      setLoginHistory(data);
    } catch { toast.error('Failed to fetch login history'); }
  };

  const handleUnlockAccount = async (userId, email) => {
    try {
      await api.put(`/api/admin/users/${userId}/unlock`);
      toast.success(`Account unlocked for ${email}`);
      if (loginHistoryUser?.id === userId) handleViewLoginHistory(loginHistoryUser);
      fetchUsers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to unlock'); }
  };

  if (loading) {
    return (<div className="min-h-screen bg-gray-50 flex items-center justify-center"><div className="animate-pulse text-gray-400">Loading admin...</div></div>);
  }

  return (
    <div className={`min-h-screen flex ${dark ? 'admin-dark bg-[#0f0f0f] text-gray-100' : 'bg-gray-50'}`} data-testid="admin-root">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 ${dark ? 'bg-[#0A0A0A]' : 'bg-[#0A0A0A]'} text-white transform transition-transform lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="h-full flex flex-col">
          <div className="p-5 border-b border-white/10">
            <Link to="/" className="flex items-center gap-2" data-testid="admin-logo">
              <div className="w-9 h-9 rounded-lg bg-gradient-to-r from-blue-600 to-indigo-600 flex items-center justify-center"><Car className="w-5 h-5 text-white" /></div>
              <span className="font-bold text-lg tracking-tight">Admin</span>
            </Link>
          </div>
          <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
            {ADMIN_NAV.map(item => (
              <button key={item.id} onClick={() => { setActiveTab(item.id); setSidebarOpen(false); }}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${activeTab === item.id ? 'bg-white/15 text-white' : 'text-white/60 hover:bg-white/10 hover:text-white'}`}
                data-testid={`admin-nav-${item.id}`}>
                <item.icon className="w-4.5 h-4.5" />{t(item.tKey)}
              </button>
            ))}
          </nav>
          <div className="p-4 border-t border-white/10">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-9 h-9 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center text-sm font-bold">{user?.name?.[0] || 'A'}</div>
              <div className="flex-1 min-w-0"><p className="text-sm font-medium truncate">{user?.name}</p><p className="text-xs text-white/50">{user?.email}</p></div>
            </div>
            <Button variant="outline" size="sm" className="w-full border-white/20 text-white hover:bg-white/10 rounded-lg" onClick={() => { logout(); navigate('/'); }} data-testid="admin-logout-btn">
              <LogOut className="w-4 h-4 mr-2" />{t('admin.signOut')}
            </Button>
          </div>
        </div>
      </aside>

      {/* Mobile header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-40 bg-[#0A0A0A] text-white px-4 py-3 flex items-center justify-between">
        <button onClick={() => setSidebarOpen(true)}><Menu className="w-6 h-6" /></button>
        <span className="font-bold">Wakzy Admin</span>
        <NotificationBell />
      </div>
      {sidebarOpen && <div className="lg:hidden fixed inset-0 z-40 bg-black/50" onClick={() => setSidebarOpen(false)} />}

      {/* Main */}
      <main className="flex-1 lg:ml-64 pt-14 lg:pt-0">
        <div className="p-4 sm:p-6 max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h1 className={`text-2xl font-bold ${dark ? 'text-white' : 'text-gray-900'}`} data-testid="admin-dashboard-title">
              {ADMIN_NAV.find(n => n.id === activeTab)?.tKey ? t(ADMIN_NAV.find(n => n.id === activeTab).tKey) : 'Admin'}
            </h1>
            <div className="flex items-center gap-3">
              <LanguagePicker />
              <button onClick={() => setDark(d => !d)} className={`flex items-center justify-center w-9 h-9 rounded-xl border transition-colors ${dark ? 'border-white/20 text-yellow-400 hover:bg-white/10' : 'border-gray-200 text-gray-500 hover:bg-gray-100'}`} data-testid="dark-mode-toggle" title={dark ? 'Light mode' : 'Dark mode'}>
                {dark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
              </button>
              <button onClick={() => setCmdOpen(true)} className={`hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-xl border text-sm transition-colors ${dark ? 'border-white/20 bg-white/5 text-gray-400 hover:text-gray-200 hover:border-white/30' : 'border-gray-200 bg-white text-gray-400 hover:text-gray-600 hover:border-gray-300'}`} data-testid="cmd-k-trigger">
                <Search className="w-3.5 h-3.5" />
                <span>{t('admin.searchCmd')}</span>
                <kbd className="ml-2 text-[10px] font-medium bg-gray-100 rounded px-1.5 py-0.5">⌘K</kbd>
              </button>
              <div className="hidden lg:block"><NotificationBell /></div>
            </div>
          </div>

          {/* ============ OVERVIEW ============ */}
          {activeTab === 'overview' && stats && (
            <div className="space-y-6">
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  { label: t('admin.totalUsers'), value: stats.users.total, sub: `+${stats.users.new_7d} ${t('admin.thisWeek')}`, icon: Users, color: 'from-blue-500 to-indigo-500' },
                  { label: t('admin.vehicles'), value: stats.vehicles.total, sub: `${stats.vehicles.available} ${t('admin.available')}`, icon: Car, color: 'from-emerald-500 to-teal-500' },
                  { label: t('admin.junkLeads'), value: stats.leads.total, sub: `${stats.leads.active} ${t('admin.active')}`, icon: Package, color: 'from-amber-500 to-orange-500' },
                  { label: t('admin.estMrr'), value: `$${(stats.subscriptions.monthly_revenue || 0).toLocaleString()}`, sub: `${stats.subscriptions.total_subscribers} ${t('admin.subscribers')}`, icon: DollarSign, color: 'from-purple-500 to-pink-500' },
                ].map((s, i) => (
                  <div key={i} className="bg-white rounded-2xl border border-gray-100 p-5 hover:shadow-md transition-shadow" data-testid={`admin-stat-${i}`}>
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm text-gray-500">{s.label}</span>
                      <div className={`w-9 h-9 rounded-xl bg-gradient-to-r ${s.color} flex items-center justify-center`}><s.icon className="w-4.5 h-4.5 text-white" /></div>
                    </div>
                    <p className="text-2xl font-bold text-gray-900">{s.value}</p>
                    <p className="text-xs text-gray-400 mt-1 flex items-center gap-1"><ArrowUpRight className="w-3 h-3 text-emerald-500" />{s.sub}</p>
                  </div>
                ))}
              </div>

              {/* Quick Actions */}
              <div className="bg-white rounded-2xl border border-gray-100 p-5">
                <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2"><Zap className="w-4 h-4 text-amber-500" />Quick Actions</h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {[
                    { label: 'New Announcement', icon: Megaphone, color: 'text-blue-600 bg-blue-50 hover:bg-blue-100', action: () => { setActiveTab('announcements'); setTimeout(() => setShowAnnDialog(true), 100); } },
                    { label: 'Export Users CSV', icon: Download, color: 'text-emerald-600 bg-emerald-50 hover:bg-emerald-100', action: handleExportUsers },
                    { label: 'View Flagged', icon: Flag, color: 'text-red-600 bg-red-50 hover:bg-red-100', action: () => setActiveTab('moderation') },
                    { label: 'Refresh Stats', icon: RefreshCw, color: 'text-indigo-600 bg-indigo-50 hover:bg-indigo-100', action: () => { fetchStats(); toast.success('Stats refreshed'); } },
                  ].map((a, i) => (
                    <button key={i} onClick={a.action} className={`flex flex-col items-center gap-2 p-4 rounded-xl transition-colors ${a.color}`} data-testid={`quick-action-${i}`}>
                      <a.icon className="w-5 h-5" />
                      <span className="text-xs font-medium">{a.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid lg:grid-cols-3 gap-4">
                <div className="lg:col-span-2 bg-white rounded-2xl border border-gray-100 p-5">
                  <h3 className="font-semibold text-gray-900 mb-4">Platform Breakdown</h3>
                  <div className="grid grid-cols-2 gap-4">
                    {[
                      { label: 'Dealers', val: stats.users.dealers, total: stats.users.total },
                      { label: 'Buyers', val: stats.users.buyers, total: stats.users.total },
                      { label: 'Vehicles Sold', val: stats.vehicles.sold, total: stats.vehicles.total },
                      { label: 'CRM Contacts', val: stats.crm_contacts, total: null },
                      { label: 'Wholesale Listings', val: stats.wholesale.active, total: stats.wholesale.total },
                      { label: 'Referrals', val: stats.referrals, total: null },
                    ].map((item, i) => (
                      <div key={i} className="flex items-center justify-between py-2 border-b border-gray-50">
                        <span className="text-sm text-gray-600">{item.label}</span>
                        <span className="font-semibold text-gray-900">{item.val}{item.total ? <span className="text-gray-400 font-normal text-xs">/{item.total}</span> : ''}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="bg-white rounded-2xl border border-gray-100 p-5">
                  <h3 className="font-semibold text-gray-900 mb-4">Subscriptions</h3>
                  {Object.entries(stats.subscriptions.by_plan || {}).length > 0 ? (
                    <div className="space-y-3">
                      {Object.entries(stats.subscriptions.by_plan).map(([plan, count]) => (
                        <div key={plan} className="flex items-center justify-between">
                          <span className="text-sm text-gray-600 capitalize">{plan.replace('_', ' ')}</span>
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-gray-900">{count}</span>
                            <div className="w-16 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                              <div className="h-full bg-blue-500 rounded-full" style={{width: `${Math.min(count / stats.subscriptions.total_subscribers * 100, 100)}%`}} />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : <p className="text-gray-400 text-sm">No active subscriptions</p>}
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <p className="text-xs text-gray-400">Documents stored</p>
                    <p className="text-lg font-bold text-gray-900">{stats.documents}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ============ USERS ============ */}
          {activeTab === 'users' && (
            <div className="space-y-4">
              <div className="flex gap-3 flex-wrap">
                <div className="relative flex-1 min-w-[200px]">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input value={userSearch} onChange={e => setUserSearch(e.target.value)} placeholder="Search users..." className="pl-10 rounded-xl" data-testid="admin-user-search" />
                </div>
                <Select value={userRoleFilter || 'all'} onValueChange={v => setUserRoleFilter(v === 'all' ? '' : v)}>
                  <SelectTrigger className="w-36 rounded-xl" data-testid="admin-role-filter"><SelectValue placeholder="Role" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Roles</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="dealer">Dealer</SelectItem>
                    <SelectItem value="buyer">Buyer</SelectItem>
                    <SelectItem value="seller">Seller</SelectItem>
                  </SelectContent>
                </Select>
                <span className="text-sm text-gray-400 self-center">{usersTotal} total</span>
              </div>

              <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="admin-users-table">
                    <thead><tr className="bg-gray-50 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      <th className="px-4 py-3">User</th><th className="px-4 py-3">Role</th><th className="px-4 py-3">Plan</th><th className="px-4 py-3">Vehicles</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Joined</th><th className="px-4 py-3 text-right">Actions</th>
                    </tr></thead>
                    <tbody className="divide-y divide-gray-50">
                      {users.map(u => (
                        <tr key={u.id} className="hover:bg-gray-50/50 text-sm" data-testid={`admin-user-row-${u.id}`}>
                          <td className="px-4 py-3">
                            <p className="font-medium text-gray-900">{u.name}</p>
                            <p className="text-xs text-gray-400">{u.email}</p>
                          </td>
                          <td className="px-4 py-3">
                            <Select value={u.role} onValueChange={v => handleRoleChange(u.id, v)}>
                              <SelectTrigger className={`h-7 text-xs rounded-lg w-24 ${ROLE_COLORS[u.role] || 'bg-gray-100 text-gray-700'}`} data-testid={`role-select-${u.id}`}><SelectValue /></SelectTrigger>
                              <SelectContent><SelectItem value="admin">Admin</SelectItem><SelectItem value="dealer">Dealer</SelectItem><SelectItem value="buyer">Buyer</SelectItem><SelectItem value="seller">Seller</SelectItem></SelectContent>
                            </Select>
                          </td>
                          <td className="px-4 py-3">
                            <button onClick={() => setSelectedUser(u)} className="text-xs px-2 py-1 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-600" data-testid={`plan-btn-${u.id}`}>
                              {u.subscription_plan ? u.subscription_plan.replace('_', ' ') : 'Free'}
                            </button>
                          </td>
                          <td className="px-4 py-3 text-gray-600">{u.vehicle_count || 0}</td>
                          <td className="px-4 py-3">
                            <span className={`text-xs px-2 py-0.5 rounded-full ${u.suspended ? 'bg-red-50 text-red-600' : 'bg-emerald-50 text-emerald-600'}`}>
                              {u.suspended ? 'Suspended' : 'Active'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-gray-400 text-xs">{u.created_at ? new Date(u.created_at).toLocaleDateString() : '-'}</td>
                          <td className="px-4 py-3 text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Button variant="ghost" size="icon" className="h-7 w-7 text-blue-500" onClick={() => { setResetPasswordUser(u); setNewPassword(''); }} title="Reset password" data-testid={`reset-pwd-${u.id}`}><Key className="w-3.5 h-3.5" /></Button>
                              <Button variant="ghost" size="icon" className="h-7 w-7 text-cyan-500" onClick={() => handleViewLoginHistory(u)} title="Login history" data-testid={`login-history-${u.id}`}><History className="w-3.5 h-3.5" /></Button>
                              <Button variant="ghost" size="icon" className="h-7 w-7 text-teal-500" onClick={() => handleUnlockAccount(u.id, u.email)} title="Unlock account" data-testid={`unlock-${u.id}`}><Unlock className="w-3.5 h-3.5" /></Button>
                              {u.role !== 'admin' && (
                                <Button variant="ghost" size="icon" className="h-7 w-7 text-indigo-500" onClick={() => handleImpersonate(u.id, u.email)} title="Login as user" data-testid={`impersonate-${u.id}`}><Eye className="w-3.5 h-3.5" /></Button>
                              )}
                              {u.suspended ? (
                                <Button variant="ghost" size="icon" className="h-7 w-7 text-emerald-500" onClick={() => handleSuspend(u.id, false)} data-testid={`reactivate-${u.id}`}><UserCheck className="w-3.5 h-3.5" /></Button>
                              ) : (
                                <Button variant="ghost" size="icon" className="h-7 w-7 text-amber-500" onClick={() => handleSuspend(u.id, true)} data-testid={`suspend-${u.id}`}><UserX className="w-3.5 h-3.5" /></Button>
                              )}
                              <Button variant="ghost" size="icon" className="h-7 w-7 text-red-400" onClick={() => handleDeleteUser(u.id)} data-testid={`delete-user-${u.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Password Reset Dialog */}
          <Dialog open={!!resetPasswordUser} onOpenChange={() => setResetPasswordUser(null)}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Reset Password</DialogTitle>
              </DialogHeader>
              {resetPasswordUser && (
                <div className="space-y-4">
                  <div className="bg-gray-50 rounded-xl p-3 text-sm">
                    <p className="font-medium text-gray-900">{resetPasswordUser.name}</p>
                    <p className="text-gray-500">{resetPasswordUser.email}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">New Password</label>
                    <Input
                      type="text"
                      value={newPassword}
                      onChange={e => setNewPassword(e.target.value)}
                      placeholder="Min 8 characters"
                      className="rounded-xl"
                      data-testid="admin-new-password-input"
                    />
                  </div>
                  <Button className="w-full rounded-xl bg-blue-600 hover:bg-blue-700 text-white" onClick={handleResetPassword} disabled={newPassword.length < 8} data-testid="admin-reset-password-btn">
                    <Key className="w-4 h-4 mr-2" />Reset Password
                  </Button>
                </div>
              )}
            </DialogContent>
          </Dialog>

          {/* Login History Dialog */}
          <Dialog open={!!loginHistoryUser} onOpenChange={() => setLoginHistoryUser(null)}>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Login History — {loginHistoryUser?.name}</DialogTitle>
              </DialogHeader>
              {loginHistoryUser && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between bg-gray-50 rounded-xl p-3">
                    <div className="text-sm">
                      <p className="font-medium text-gray-900">{loginHistoryUser.email}</p>
                      <p className="text-gray-500">Account status: {loginHistoryUser.suspended ? 'Suspended' : 'Active'}</p>
                    </div>
                    {loginHistory?.locked && (
                      <span className="text-xs bg-red-100 text-red-700 px-3 py-1 rounded-full font-medium">Locked</span>
                    )}
                  </div>
                  {loginHistory === null ? (
                    <div className="text-center py-6 text-gray-400">Loading...</div>
                  ) : loginHistory.login_attempts.length === 0 ? (
                    <div className="text-center py-6 text-gray-400">
                      <CheckCircle className="w-8 h-8 mx-auto mb-2 text-green-400" />
                      <p className="font-medium">No failed login attempts</p>
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {loginHistory.login_attempts.map((a, i) => (
                        <div key={i} className="flex items-center justify-between text-sm bg-gray-50 rounded-xl px-4 py-3" data-testid={`login-attempt-${i}`}>
                          <div>
                            <p className="text-gray-700 font-mono text-xs">{a.ip}</p>
                            <p className="text-gray-400 text-xs">{a.last_attempt ? new Date(a.last_attempt).toLocaleString() : '-'}</p>
                          </div>
                          <div className="text-right">
                            <p className="font-medium text-gray-900">{a.attempts} attempt{a.attempts !== 1 ? 's' : ''}</p>
                            {a.locked_until && new Date(a.locked_until) > new Date() && (
                              <p className="text-xs text-red-600">Locked until {new Date(a.locked_until).toLocaleTimeString()}</p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {loginHistory?.locked && (
                    <Button className="w-full rounded-xl bg-teal-600 hover:bg-teal-700 text-white" onClick={() => handleUnlockAccount(loginHistoryUser.id, loginHistoryUser.email)} data-testid="dialog-unlock-btn">
                      <Unlock className="w-4 h-4 mr-2" />Unlock Account
                    </Button>
                  )}
                </div>
              )}
            </DialogContent>
          </Dialog>


          {/* ============ ID VERIFICATION ============ */}
          {activeTab === 'id-verification' && (
            <div className="space-y-4" data-testid="admin-id-verification-tab">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-3">
                  <Select value={idStatusFilter} onValueChange={setIdStatusFilter}>
                    <SelectTrigger className="w-40 rounded-xl" data-testid="id-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Submissions</SelectItem>
                      <SelectItem value="pending">Pending Review</SelectItem>
                      <SelectItem value="verified">Approved</SelectItem>
                      <SelectItem value="rejected">Rejected</SelectItem>
                    </SelectContent>
                  </Select>
                  <span className="text-sm text-gray-400">{idVerifications.length} result{idVerifications.length !== 1 ? 's' : ''}</span>
                </div>
                <Button variant="outline" size="sm" className="rounded-xl" onClick={fetchIdVerifications} data-testid="refresh-id-list">
                  <RefreshCw className="w-3.5 h-3.5 mr-2" />Refresh
                </Button>
              </div>

              {idVerifications.length === 0 ? (
                <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center">
                  <FileCheck className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500 font-medium">No ID submissions found</p>
                  <p className="text-sm text-gray-400 mt-1">Users who upload their IDs will appear here for review.</p>
                </div>
              ) : (
                <div className="grid gap-4">
                  {idVerifications.map(v => (
                    <div key={v.id} className="bg-white rounded-2xl border border-gray-100 p-5 hover:shadow-md transition-shadow" data-testid={`id-card-${v.id}`}>
                      <div className="flex items-start justify-between gap-4 flex-wrap">
                        <div className="flex items-center gap-4">
                          <div className={`w-12 h-12 rounded-2xl flex items-center justify-center text-white font-bold text-lg shadow-lg ${
                            v.id_verification_status === 'verified' ? 'bg-gradient-to-br from-emerald-500 to-green-600' :
                            v.id_verification_status === 'rejected' ? 'bg-gradient-to-br from-red-500 to-rose-600' :
                            'bg-gradient-to-br from-amber-500 to-orange-500'
                          }`}>
                            {v.name?.[0]?.toUpperCase() || '?'}
                          </div>
                          <div>
                            <p className="font-semibold text-gray-900">{v.name}</p>
                            <p className="text-sm text-gray-500">{v.email}</p>
                            <div className="flex items-center gap-3 mt-1 text-xs text-gray-400">
                              <span>{v.company_name || 'No company'}</span>
                              <span>{v.phone || 'No phone'}</span>
                              <span className={`px-1.5 py-0.5 rounded ${ROLE_COLORS[v.role] || 'bg-gray-100 text-gray-600'}`}>{v.role}</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full ${
                            v.id_verification_status === 'verified' ? 'bg-emerald-50 text-emerald-700' :
                            v.id_verification_status === 'rejected' ? 'bg-red-50 text-red-700' :
                            'bg-amber-50 text-amber-700'
                          }`} data-testid={`id-status-badge-${v.id}`}>
                            {v.id_verification_status === 'verified' && <CheckCircle className="w-3.5 h-3.5" />}
                            {v.id_verification_status === 'rejected' && <XCircle className="w-3.5 h-3.5" />}
                            {v.id_verification_status === 'pending' && <Clock className="w-3.5 h-3.5" />}
                            {v.id_verification_status === 'verified' ? 'Approved' : v.id_verification_status === 'rejected' ? 'Rejected' : 'Pending'}
                          </span>
                        </div>
                      </div>

                      <div className="mt-4 flex items-center gap-3 flex-wrap">
                        <Button
                          variant="outline" size="sm"
                          className="rounded-xl text-blue-600 border-blue-200 hover:bg-blue-50"
                          onClick={() => setIdPreviewUser(v)}
                          data-testid={`view-id-${v.id}`}
                        >
                          <Eye className="w-3.5 h-3.5 mr-1.5" />View Document
                        </Button>
                        {v.id_verification_status === 'pending' && (
                          <>
                            <Button
                              size="sm"
                              className="rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white"
                              onClick={() => handleApproveId(v.id, v.email)}
                              data-testid={`approve-id-${v.id}`}
                            >
                              <CheckCircle className="w-3.5 h-3.5 mr-1.5" />Approve
                            </Button>
                            <Button
                              size="sm" variant="outline"
                              className="rounded-xl text-red-600 border-red-200 hover:bg-red-50"
                              onClick={() => { setIdPreviewUser(v); setRejectNote(''); }}
                              data-testid={`reject-id-btn-${v.id}`}
                            >
                              <XCircle className="w-3.5 h-3.5 mr-1.5" />Reject
                            </Button>
                          </>
                        )}
                        {v.id_document_url && (
                          <a href={v.id_document_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600">
                            <ExternalLink className="w-3 h-3" />Open Original
                          </a>
                        )}
                      </div>
                      {v.id_review_note && (
                        <div className="mt-3 text-xs text-red-500 bg-red-50 rounded-lg px-3 py-2">
                          <span className="font-medium">Rejection note:</span> {v.id_review_note}
                        </div>
                      )}
                      <div className="mt-3 text-xs text-gray-400">
                        Submitted: {v.id_submitted_at ? new Date(v.id_submitted_at).toLocaleString() : 'Unknown'}
                        {v.id_reviewed_at && <> &middot; Reviewed: {new Date(v.id_reviewed_at).toLocaleString()}</>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ID Preview Dialog */}
          <Dialog open={!!idPreviewUser} onOpenChange={() => { setIdPreviewUser(null); setRejectNote(''); }}>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>ID Document Review — {idPreviewUser?.name}</DialogTitle>
              </DialogHeader>
              {idPreviewUser && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div><span className="text-gray-500">Email:</span> <span className="font-medium">{idPreviewUser.email}</span></div>
                    <div><span className="text-gray-500">Phone:</span> <span className="font-medium">{idPreviewUser.phone || '-'}</span></div>
                    <div><span className="text-gray-500">Company:</span> <span className="font-medium">{idPreviewUser.company_name || '-'}</span></div>
                    <div><span className="text-gray-500">Role:</span> <span className="font-medium capitalize">{idPreviewUser.role}</span></div>
                  </div>
                  {idPreviewUser.id_document_url && (
                    <div className="rounded-xl border border-gray-200 overflow-hidden bg-gray-50">
                      {idPreviewUser.id_document_url.match(/\.pdf/i) ? (
                        <div className="p-8 text-center">
                          <FileCheck className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                          <a href={idPreviewUser.id_document_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline font-medium text-sm">Open PDF Document</a>
                        </div>
                      ) : (
                        <img src={idPreviewUser.id_document_url} alt="ID Document" className="w-full max-h-[400px] object-contain" data-testid="id-preview-image" />
                      )}
                    </div>
                  )}
                  {idPreviewUser.id_verification_status === 'pending' && (
                    <div className="space-y-3 pt-2 border-t border-gray-100">
                      <div>
                        <label className="text-sm font-medium text-gray-700 mb-1 block">Rejection Note (optional)</label>
                        <Textarea
                          value={rejectNote}
                          onChange={e => setRejectNote(e.target.value)}
                          placeholder="Reason for rejection (visible to user)..."
                          className="rounded-xl"
                          rows={2}
                          data-testid="reject-note-input"
                        />
                      </div>
                      <div className="flex gap-3">
                        <Button
                          className="flex-1 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white"
                          onClick={() => handleApproveId(idPreviewUser.id, idPreviewUser.email)}
                          data-testid="dialog-approve-btn"
                        >
                          <CheckCircle className="w-4 h-4 mr-2" />Approve Identity
                        </Button>
                        <Button
                          variant="outline"
                          className="flex-1 rounded-xl text-red-600 border-red-200 hover:bg-red-50"
                          onClick={() => handleRejectId(idPreviewUser.id, idPreviewUser.email)}
                          data-testid="dialog-reject-btn"
                        >
                          <XCircle className="w-4 h-4 mr-2" />Reject
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </DialogContent>
          </Dialog>

          {/* ============ VEHICLES ============ */}
          {activeTab === 'vehicles' && (
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
              <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
                <p className="text-sm text-gray-500">{vehiclesTotal} vehicles platform-wide</p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="admin-vehicles-table">
                  <thead><tr className="bg-gray-50 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    <th className="px-4 py-3">Vehicle</th><th className="px-4 py-3">VIN</th><th className="px-4 py-3">Price</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Dealer</th><th className="px-4 py-3">Listed</th>
                  </tr></thead>
                  <tbody className="divide-y divide-gray-50">
                    {vehicles.map(v => (
                      <tr key={v.id} className="hover:bg-gray-50/50 text-sm" data-testid={`admin-vehicle-${v.id}`}>
                        <td className="px-4 py-3 font-medium text-gray-900">{v.year} {v.make} {v.model}</td>
                        <td className="px-4 py-3 text-gray-500 font-mono text-xs">{v.vin || '-'}</td>
                        <td className="px-4 py-3 font-medium">{v.price ? `$${v.price.toLocaleString()}` : '-'}</td>
                        <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 rounded-full ${v.status === 'sold' ? 'bg-emerald-50 text-emerald-600' : v.status === 'available' ? 'bg-blue-50 text-blue-600' : 'bg-gray-100 text-gray-600'}`}>{v.status}</span></td>
                        <td className="px-4 py-3"><p className="text-gray-900 text-xs">{v.dealer_name}</p><p className="text-gray-400 text-xs">{v.dealer_company}</p></td>
                        <td className="px-4 py-3 text-gray-400 text-xs">{v.created_at ? new Date(v.created_at).toLocaleDateString() : '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ============ REVENUE ============ */}
          {activeTab === 'revenue' && (
            <div className="space-y-6">
              {revenue ? (
                <>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {[
                      { label: 'Monthly Recurring Revenue', value: `$${(revenue.total_mrr || 0).toLocaleString()}`, sub: 'MRR', color: 'from-emerald-500 to-teal-500' },
                      { label: 'Annual Run Rate', value: `$${(revenue.arr || 0).toLocaleString()}`, sub: 'ARR projection', color: 'from-blue-500 to-indigo-500' },
                      { label: 'Active Subscribers', value: revenue.current_subscribers, sub: `of ${revenue.total_users} users`, color: 'from-purple-500 to-pink-500' },
                      { label: 'Conversion Rate', value: revenue.total_users > 0 ? `${((revenue.current_subscribers / revenue.total_users) * 100).toFixed(1)}%` : '0%', sub: 'free to paid', color: 'from-amber-500 to-orange-500' },
                    ].map((s, i) => (
                      <div key={i} className="bg-white rounded-2xl border border-gray-100 p-5" data-testid={`revenue-stat-${i}`}>
                        <p className="text-sm text-gray-500 mb-1">{s.label}</p>
                        <p className="text-2xl font-bold text-gray-900">{s.value}</p>
                        <p className="text-xs text-gray-400 mt-1">{s.sub}</p>
                      </div>
                    ))}
                  </div>

                  <div className="grid lg:grid-cols-2 gap-4">
                    <div className="bg-white rounded-2xl border border-gray-100 p-5">
                      <h3 className="font-semibold text-gray-900 mb-4">Revenue by Plan</h3>
                      {Object.keys(revenue.by_plan || {}).length > 0 ? (
                        <div className="space-y-4">
                          {Object.entries(revenue.by_plan).map(([plan, info]) => (
                            <div key={plan} className="space-y-1">
                              <div className="flex items-center justify-between">
                                <span className="text-sm font-medium text-gray-700 capitalize">{plan.replace('_', ' ')}</span>
                                <span className="text-sm font-bold text-gray-900">${info.revenue?.toLocaleString()}/mo</span>
                              </div>
                              <div className="flex items-center justify-between text-xs text-gray-400">
                                <span>{info.subscribers} subscribers x ${info.price}/mo</span>
                              </div>
                              <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                                <div className="h-full bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full" style={{ width: `${revenue.total_mrr > 0 ? (info.revenue / revenue.total_mrr * 100) : 0}%` }} />
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : <p className="text-gray-400 text-sm py-4">No paying subscribers yet</p>}
                    </div>

                    <div className="bg-white rounded-2xl border border-gray-100 p-5">
                      <h3 className="font-semibold text-gray-900 mb-4">User Distribution</h3>
                      <div className="space-y-3">
                        {Object.entries(revenue.plan_distribution || {}).map(([plan, count]) => (
                          <div key={plan} className="flex items-center justify-between py-2 border-b border-gray-50">
                            <div className="flex items-center gap-2">
                              <div className={`w-3 h-3 rounded-full ${plan === 'free' ? 'bg-gray-300' : plan === 'starter' ? 'bg-blue-400' : plan === 'professional' ? 'bg-indigo-500' : 'bg-purple-500'}`} />
                              <span className="text-sm text-gray-700 capitalize">{plan === 'null' ? 'Free' : plan.replace('_', ' ')}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-gray-900">{count}</span>
                              <span className="text-xs text-gray-400">({revenue.total_users > 0 ? ((count / revenue.total_users) * 100).toFixed(0) : 0}%)</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {(revenue.subscriber_growth || []).length > 0 && (
                    <div className="bg-white rounded-2xl border border-gray-100 p-5">
                      <h3 className="font-semibold text-gray-900 mb-4">Subscriber Growth (Last 90 Days)</h3>
                      <div className="flex items-end gap-1 h-32">
                        {revenue.subscriber_growth.map((d, i) => (
                          <div key={i} className="flex-1 flex flex-col items-center justify-end" title={`${d.date}: ${d.count}`}>
                            <div className="w-full bg-gradient-to-t from-blue-500 to-indigo-400 rounded-t" style={{ height: `${Math.max(d.count * 20, 4)}px` }} />
                          </div>
                        ))}
                      </div>
                      <div className="flex justify-between text-xs text-gray-400 mt-2">
                        <span>{revenue.subscriber_growth[0]?.date}</span>
                        <span>{revenue.subscriber_growth[revenue.subscriber_growth.length - 1]?.date}</span>
                      </div>
                    </div>
                  )}
                </>
              ) : <p className="text-gray-400 text-sm">Loading revenue data...</p>}
            </div>
          )}

          {/* ============ MODERATION ============ */}
          {activeTab === 'moderation' && (
            <div className="space-y-6">
              <div>
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2"><Package className="w-5 h-5" /> Wholesale Listings ({listings.length})</h3>
                {listings.length === 0 ? <p className="text-gray-400 text-sm">No listings to review</p> : (
                  <div className="space-y-2">
                    {listings.slice(0, 20).map(l => (
                      <div key={l.id} className={`bg-white rounded-xl border ${l.flagged ? 'border-red-200 bg-red-50/50' : 'border-gray-100'} p-4 flex items-center gap-4`} data-testid={`mod-listing-${l.id}`}>
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">{l.year} {l.make} {l.model} — ${(l.wholesale_price || 0).toLocaleString()}</p>
                          <p className="text-xs text-gray-500">By {l.dealer_name || l.company_name || 'Unknown'} | {l.status} | {(l.offers?.length || l.offers?.[0]?.count) || 0} offers</p>
                          {l.flagged && <p className="text-xs text-red-500 mt-1">Flagged: {l.flag_reason}</p>}
                        </div>
                        {l.flagged ? (
                          <Button size="sm" variant="outline" className="rounded-lg text-emerald-600 border-emerald-200" onClick={() => handleFlagListing(l.id, false)} data-testid={`unflag-${l.id}`}><UserCheck className="w-3.5 h-3.5 mr-1" /> Unflag</Button>
                        ) : (
                          <Button size="sm" variant="outline" className="rounded-lg text-red-500 border-red-200" onClick={() => handleFlagListing(l.id, true, 'Review required')} data-testid={`flag-${l.id}`}><Flag className="w-3.5 h-3.5 mr-1" /> Flag</Button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2"><AlertTriangle className="w-5 h-5" /> Junk Car Leads ({leads.length})</h3>
                {leads.length === 0 ? <p className="text-gray-400 text-sm">No leads to review</p> : (
                  <div className="space-y-2">
                    {leads.slice(0, 20).map(l => (
                      <div key={l.id} className="bg-white rounded-xl border border-gray-100 p-4 flex items-center gap-4" data-testid={`mod-lead-${l.id}`}>
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">{l.year} {l.make} {l.model} — {l.condition}</p>
                          <p className="text-xs text-gray-500">{l.seller_name} | {l.location} | {l.offer_count} offers | {l.status}</p>
                        </div>
                        <Select value={l.status} onValueChange={v => handleLeadStatus(l.id, v)}>
                          <SelectTrigger className="w-28 h-8 rounded-lg text-xs" data-testid={`lead-status-${l.id}`}><SelectValue /></SelectTrigger>
                          <SelectContent><SelectItem value="pending">Pending</SelectItem><SelectItem value="active">Active</SelectItem><SelectItem value="closed">Closed</SelectItem><SelectItem value="flagged">Flagged</SelectItem></SelectContent>
                        </Select>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ============ ANNOUNCEMENTS ============ */}
          {activeTab === 'announcements' && (
            <div className="space-y-4">
              <Button onClick={() => setShowAnnDialog(true)} className="rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 text-white" data-testid="create-announcement-btn">
                <Megaphone className="w-4 h-4 mr-2" /> New Announcement
              </Button>
              {announcements.length === 0 ? (
                <div className="text-center py-12 text-gray-400"><Megaphone className="w-10 h-10 mx-auto mb-2 opacity-50" /><p>No announcements yet</p></div>
              ) : announcements.map(a => (
                <div key={a.id} className="bg-white rounded-2xl border border-gray-100 p-5" data-testid={`ann-${a.id}`}>
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h4 className="font-semibold text-gray-900">{a.title}</h4>
                      <p className="text-xs text-gray-400">{a.created_by_name} | {new Date(a.created_at).toLocaleString()} | Target: {a.target_role}</p>
                    </div>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-red-400" onClick={() => handleDeleteAnn(a.id)} data-testid={`delete-ann-${a.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                  </div>
                  <p className="text-sm text-gray-600">{a.message}</p>
                </div>
              ))}
            </div>
          )}

          {/* ============ EMAIL DIGEST ============ */}
          {activeTab === 'digest' && digestPrefs && (
            <div className="space-y-6">
              {/* Send Now + Preview */}
              <div className="grid lg:grid-cols-2 gap-4">
                <div className="bg-white rounded-2xl border border-gray-100 p-6">
                  <h3 className="font-semibold text-gray-900 mb-1">Send Digest Now</h3>
                  <p className="text-xs text-gray-400 mb-5">Instantly send a platform summary email</p>
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <label className="text-sm text-gray-600 w-20">Send to</label>
                      <Input
                        value={digestPrefs.email || ''}
                        onChange={e => setDigestPrefs(prev => ({ ...prev, email: e.target.value }))}
                        onBlur={e => handleUpdateDigestPref('email', e.target.value)}
                        className="rounded-xl flex-1"
                        data-testid="digest-email"
                      />
                    </div>
                    <div className="flex items-center gap-3">
                      <label className="text-sm text-gray-600 w-20">Period</label>
                      <Select value={digestPrefs.frequency || 'daily'} onValueChange={v => handleUpdateDigestPref('frequency', v)}>
                        <SelectTrigger className="rounded-xl flex-1" data-testid="digest-frequency"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="daily">Daily Summary</SelectItem>
                          <SelectItem value="weekly">Weekly Summary</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Button
                      onClick={handleSendDigest}
                      disabled={sendingDigest}
                      className="w-full rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 text-white"
                      data-testid="send-digest-btn"
                    >
                      <Send className="w-4 h-4 mr-2" />
                      {sendingDigest ? 'Sending...' : 'Send Digest Email'}
                    </Button>
                  </div>
                </div>

                <div className="bg-white rounded-2xl border border-gray-100 p-6">
                  <h3 className="font-semibold text-gray-900 mb-1">Digest Preferences</h3>
                  <p className="text-xs text-gray-400 mb-5">Configure what's included in your digest</p>
                  <div className="space-y-3">
                    {[
                      { key: 'include_revenue', label: 'Revenue & Subscriptions' },
                      { key: 'include_users', label: 'User Signups & Activity' },
                      { key: 'include_leads', label: 'Junk Leads & Wholesale' },
                      { key: 'include_flagged', label: 'Flagged Content Alerts' },
                    ].map(item => (
                      <div key={item.key} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0" data-testid={`digest-pref-${item.key}`}>
                        <span className="text-sm text-gray-700">{item.label}</span>
                        <button onClick={() => handleUpdateDigestPref(item.key, !digestPrefs[item.key])}
                          className={`relative w-10 h-5 rounded-full transition-colors ${digestPrefs[item.key] ? 'bg-blue-500' : 'bg-gray-200'}`}>
                          <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${digestPrefs[item.key] ? 'translate-x-[20px]' : 'translate-x-0.5'}`} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Live Preview */}
              {digestPreview && (
                <div className="bg-white rounded-2xl border border-gray-100 p-6">
                  <h3 className="font-semibold text-gray-900 mb-4">Live Metrics Preview</h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
                    {[
                      { label: 'Total Users', value: digestPreview.stats.total_users },
                      { label: 'New Signups', value: digestPreview.stats.new_users },
                      { label: 'MRR', value: `$${digestPreview.stats.mrr?.toLocaleString()}` },
                      { label: 'Active Vehicles', value: digestPreview.stats.vehicles_available },
                      { label: 'Active Leads', value: digestPreview.stats.active_leads },
                      { label: 'Flagged', value: digestPreview.stats.flagged_listings },
                    ].map((m, i) => (
                      <div key={i} className="text-center py-3 px-2 bg-gray-50 rounded-xl" data-testid={`digest-metric-${i}`}>
                        <p className="text-lg font-bold text-gray-900">{m.value}</p>
                        <p className="text-xs text-gray-400">{m.label}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Scheduler Status */}
              <div className="bg-white rounded-2xl border border-gray-100 p-6">
                <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Activity className="w-4 h-4 text-blue-500" />Automated Scheduler
                </h3>
                <div className="grid sm:grid-cols-3 gap-4">
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl" data-testid="scheduler-status">
                    <div className={`w-2.5 h-2.5 rounded-full ${schedulerStatus?.scheduler_running ? 'bg-emerald-500 animate-pulse' : 'bg-gray-300'}`} />
                    <div>
                      <p className="text-sm font-medium text-gray-900">{schedulerStatus?.scheduler_running ? 'Running' : 'Stopped'}</p>
                      <p className="text-xs text-gray-400">Background scheduler</p>
                    </div>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-xl">
                    <p className="text-sm font-medium text-gray-900">{schedulerStatus?.enabled_digests || 0} active</p>
                    <p className="text-xs text-gray-400">Enabled digest subscriptions</p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-xl">
                    <p className="text-sm font-medium text-gray-900">{schedulerStatus?.last_sent ? new Date(schedulerStatus.last_sent).toLocaleString() : 'Never'}</p>
                    <p className="text-xs text-gray-400">Last automated send</p>
                  </div>
                </div>
                <p className="text-xs text-gray-400 mt-3">{schedulerStatus?.schedule || 'Daily at 8:00 AM UTC, Weekly on Mondays'}</p>
                <p className="text-xs text-gray-400 mt-1">Enable digest above and set frequency to activate automated emails.</p>
              </div>
            </div>
          )}

          {/* ============ ACTIVITY LOG ============ */}
          {activeTab === 'activity' && (
            <div className="bg-white rounded-2xl border border-gray-100 divide-y divide-gray-50">
              {activityLog.length === 0 ? (
                <div className="text-center py-12 text-gray-400"><Activity className="w-10 h-10 mx-auto mb-2 opacity-50" /><p>No activity recorded yet</p></div>
              ) : activityLog.map(a => (
                <div key={a.id} className="px-5 py-3 flex items-center gap-4" data-testid={`activity-${a.id}`}>
                  <div className={`w-2 h-2 rounded-full flex-shrink-0 ${a.action.includes('delete') ? 'bg-red-500' : a.action.includes('suspend') ? 'bg-amber-500' : a.action.includes('flag') ? 'bg-red-400' : 'bg-blue-500'}`} />
                  <div className="flex-1">
                    <p className="text-sm text-gray-700">{a.details}</p>
                    <p className="text-xs text-gray-400">{a.action}</p>
                  </div>
                  <span className="text-xs text-gray-400 flex-shrink-0">{new Date(a.created_at).toLocaleString()}</span>
                </div>
              ))}
            </div>
          )}

          {/* ============ SYSTEM HEALTH ============ */}
          {activeTab === 'health' && health && (
            <div className="space-y-4">
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="bg-white rounded-2xl border border-gray-100 p-5">
                  <div className="flex items-center gap-2 mb-3"><Server className="w-5 h-5 text-emerald-500" /><h4 className="font-semibold text-gray-900">WebSocket</h4></div>
                  <p className="text-2xl font-bold text-gray-900">{health.websocket_connections?.total || 0}</p>
                  <p className="text-xs text-gray-400">active connections</p>
                </div>
                <div className="bg-white rounded-2xl border border-gray-100 p-5">
                  <div className="flex items-center gap-2 mb-3"><Shield className="w-5 h-5 text-amber-500" /><h4 className="font-semibold text-gray-900">Login Attempts</h4></div>
                  <p className="text-2xl font-bold text-gray-900">{health.recent_login_attempts_1h || 0}</p>
                  <p className="text-xs text-gray-400">in the last hour</p>
                </div>
                <div className="bg-white rounded-2xl border border-gray-100 p-5">
                  <div className="flex items-center gap-2 mb-3"><Activity className="w-5 h-5 text-blue-500" /><h4 className="font-semibold text-gray-900">Server Time</h4></div>
                  <p className="text-lg font-bold text-gray-900">{new Date(health.server_time).toLocaleString()}</p>
                  <p className="text-xs text-gray-400">UTC</p>
                </div>
              </div>
              <div className="bg-white rounded-2xl border border-gray-100 p-5">
                <div className="flex items-center gap-2 mb-4"><Database className="w-5 h-5 text-indigo-500" /><h4 className="font-semibold text-gray-900">Database Collections</h4></div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {Object.entries(health.database_collections || {}).map(([col, count]) => (
                    <div key={col} className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg">
                      <span className="text-sm text-gray-600">{col}</span>
                      <span className="font-semibold text-gray-900">{count}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ============ PLATFORM SETTINGS ============ */}
          {activeTab === 'platform' && platformSettings && (
            <div className="space-y-6">
              <div className="bg-white rounded-2xl border border-gray-100 p-6">
                <h3 className="font-semibold text-gray-900 mb-1">Feature Toggles</h3>
                <p className="text-xs text-gray-400 mb-5">Enable or disable platform features without redeploying</p>
                <div className="space-y-4">
                  {[
                    { key: 'registration_enabled', label: 'User Registration', desc: 'Allow new users to sign up' },
                    { key: 'maintenance_mode', label: 'Maintenance Mode', desc: 'Show maintenance page to non-admins' },
                    { key: 'allow_junk_submissions', label: 'Junk Car Submissions', desc: 'Allow sellers to submit junk vehicle leads' },
                    { key: 'allow_wholesale_marketplace', label: 'Wholesale Marketplace', desc: 'Enable dealer-to-dealer wholesale listings' },
                    { key: 'require_email_verification', label: 'Email Verification', desc: 'Require email verification on signup' },
                  ].map(toggle => (
                    <div key={toggle.key} className="flex items-center justify-between py-3 border-b border-gray-50 last:border-0" data-testid={`toggle-${toggle.key}`}>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{toggle.label}</p>
                        <p className="text-xs text-gray-400">{toggle.desc}</p>
                      </div>
                      <button onClick={() => handleToggleSetting(toggle.key, platformSettings[toggle.key])}
                        className={`relative w-11 h-6 rounded-full transition-colors ${platformSettings[toggle.key] ? 'bg-blue-500' : 'bg-gray-200'}`}
                        data-testid={`toggle-btn-${toggle.key}`}>
                        <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${platformSettings[toggle.key] ? 'translate-x-[22px]' : 'translate-x-0.5'}`} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-2xl border border-gray-100 p-6">
                <h3 className="font-semibold text-gray-900 mb-1">Vehicle Limits by Plan</h3>
                <p className="text-xs text-gray-400 mb-5">Max vehicles each subscription tier can list</p>
                <div className="grid sm:grid-cols-2 gap-4">
                  {[
                    { key: 'max_vehicles_free', label: 'Free' },
                    { key: 'max_vehicles_starter', label: 'Starter' },
                    { key: 'max_vehicles_professional', label: 'Professional' },
                    { key: 'max_vehicles_dealer_pro', label: 'Dealer Pro' },
                  ].map(item => (
                    <div key={item.key} className="flex items-center gap-3">
                      <label className="text-sm text-gray-600 w-28">{item.label}</label>
                      <Input
                        type="number"
                        value={platformSettings[item.key] ?? ''}
                        onChange={e => setPlatformSettings(prev => ({ ...prev, [item.key]: parseInt(e.target.value) || 0 }))}
                        onBlur={e => handleUpdateSetting(item.key, parseInt(e.target.value) || 0)}
                        className="rounded-xl w-28"
                        data-testid={`limit-${item.key}`}
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-2xl border border-gray-100 p-6">
                <h3 className="font-semibold text-gray-900 mb-1">Platform Identity</h3>
                <p className="text-xs text-gray-400 mb-5">General platform configuration</p>
                <div className="space-y-4">
                  {[
                    { key: 'platform_name', label: 'Platform Name' },
                    { key: 'support_email', label: 'Support Email' },
                  ].map(item => (
                    <div key={item.key} className="flex items-center gap-3">
                      <label className="text-sm text-gray-600 w-32">{item.label}</label>
                      <Input
                        value={platformSettings[item.key] ?? ''}
                        onChange={e => setPlatformSettings(prev => ({ ...prev, [item.key]: e.target.value }))}
                        onBlur={e => handleUpdateSetting(item.key, e.target.value)}
                        className="rounded-xl flex-1"
                        data-testid={`setting-${item.key}`}
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-2xl border border-gray-100 p-6">
                <h3 className="font-semibold text-gray-900 mb-1">SMS Notifications (Twilio)</h3>
                <p className="text-xs text-gray-400 mb-5">Send a test SMS to verify Twilio is configured</p>
                <div className="flex gap-3">
                  <Input
                    id="test-sms-phone"
                    placeholder="+1 (555) 123-4567"
                    className="rounded-xl flex-1"
                    data-testid="test-sms-phone"
                  />
                  <Button
                    className="rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="send-test-sms-btn"
                    onClick={async () => {
                      const phone = document.getElementById('test-sms-phone').value;
                      if (!phone) return toast.error('Enter a phone number');
                      try {
                        const { data } = await api.post('/api/admin/test-sms', { phone });
                        if (data.success) toast.success('Test SMS sent!');
                        else toast.error(data.error || 'SMS failed');
                      } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
                    }}
                  >
                    Send Test SMS
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* ============ NOTIFICATIONS ============ */}
          {activeTab === 'settings' && (<NotificationSettings userRole="admin" />)}
        </div>
      </main>

      {/* Plan Dialog */}
      <Dialog open={!!selectedUser} onOpenChange={v => { if (!v) setSelectedUser(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Change Plan — {selectedUser?.name}</DialogTitle></DialogHeader>
          <div className="space-y-2 pt-2">
            {['free', 'starter', 'professional', 'dealer_pro'].map(plan => (
              <button key={plan} onClick={() => handlePlanChange(selectedUser.id, plan)}
                className={`w-full text-left p-3 rounded-xl border transition-all ${selectedUser?.subscription_plan === plan || (!selectedUser?.subscription_plan && plan === 'free') ? 'border-blue-500 bg-blue-50' : 'border-gray-100 hover:border-gray-300'}`}
                data-testid={`plan-option-${plan}`}>
                <p className="font-medium text-gray-900 capitalize">{plan.replace('_', ' ')}</p>
                <p className="text-xs text-gray-400">{plan === 'free' ? 'No subscription' : `$${({'starter':49,'professional':79,'dealer_pro':149})[plan]}/mo`}</p>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Announcement Dialog */}
      <Dialog open={showAnnDialog} onOpenChange={setShowAnnDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>New Announcement</DialogTitle></DialogHeader>
          <div className="space-y-4 pt-2">
            <Input value={annForm.title} onChange={e => setAnnForm({...annForm, title: e.target.value})} placeholder="Title" className="rounded-xl" data-testid="ann-title" />
            <Textarea value={annForm.message} onChange={e => setAnnForm({...annForm, message: e.target.value})} placeholder="Message..." rows={4} className="rounded-xl" data-testid="ann-message" />
            <Select value={annForm.target_role} onValueChange={v => setAnnForm({...annForm, target_role: v})}>
              <SelectTrigger className="rounded-xl" data-testid="ann-target"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">All Users</SelectItem><SelectItem value="dealer">Dealers Only</SelectItem><SelectItem value="buyer">Buyers Only</SelectItem></SelectContent>
            </Select>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowAnnDialog(false)} className="rounded-xl">Cancel</Button>
              <Button onClick={handleCreateAnn} className="rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 text-white" data-testid="submit-ann-btn">Publish</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <CommandPalette open={cmdOpen} onClose={() => setCmdOpen(false)} onNavigate={handleCmdNavigate} />
    </div>
  );
}
